import BirthdayBookingProgramJson from "./BirthdayProgramJson";
import BirthdayTimeSlotJson, { validateBirthdayTimeSlotJson } from "./BirthdayTimeSlotJson";
import BusinessHoursJson, { validateBusinessHoursJson } from "./BusinessHoursJson";
import RentalTimeSlotJson, { validateRentalTimeSlotJson } from "./RentalTimeSlotJson";
import { Month } from './Month';
import { Validation } from './Validation';
import { WeekDay } from "./WeekDay";
import VATCategoryJson from "./VATCategoryJson";
import DateFormatter from "./DateFormatter";

export default interface SettingsJson {

   /**
    * Gibt an, an welchen Tagen der Betrieb offen ist. Wird bisher nur für das Verleihmodul genutzt.
    * depricated -> now: businessHours
    */
   operationWeekDays: WeekDay[];

   /**
    * Gibt an, an welchen Tagen und zu welchen Zeiten der Betrieb offen ist. Wird bisher nur für das Verleihmodul genutzt.
    */
   businessHours: BusinessHoursJson[];

   /**
    * Gibt an, an welchen Daten der Betrieb nicht offen ist (Feiertage, Urlaub). Liste an Daten als ISO-Datum (YYYY-MM-DD). Wird bisher nur für das Verleihmodul genutzt.
    */
   holidays: string[];

   /**
    * Liste an Orten die für Geburtstage und andere Events in Frage kommen.
    */
   eventLocations: string[];

   /**
    * Zeitslots zu denen Geburtstage buchbar sind.
    */
   birthdayTimeSlots: BirthdayTimeSlotJson[];

   /**
    * Gibt an nach wie vielen Tagen die Nutzerdaten gelöscht werden sollen
    */
   deleteUserDataAfterDays: number;

   /**
    * Gibt an wie knapp vor einem Geburtstags Anfragen noch gemacht werden können.
    */
   birthdaysCanRequestUntilDaysAhead: number;

   /**
    * Gibt an wie knapp vor einem Geburtstags Anfragen noch online zu Buchungen gemacht werden können.
    */
   birthdaysCanConfirmUntilDaysAhead: number;

   /**
    * Gibt an wie knapp vor einem Gruppenevent Angebote noch online angefragt werden können.
    */
   groupBookingCanRequestUntilDaysAhead: number;

   /**
    * Gibt an wie knapp vor einem Gruppenevent Angebote noch online akzeptiert werden können.
    */
   groupBookingCanConfirmUntilDaysAhead: number;

   /**
    * Gibt an wie knapp vor einer Veranstaltung noch online angefragt werden kann.
    */
   eventBookingCanRequestUntilDaysAhead: number;

   /**
    * Gibt an wie knapp vor einer Veranstaltung noch online Änderungen vorgenommen werden können.
    */
   eventBookingCanModifyUntilDaysAhead: number;

   /**
    * Gibt an wie viele Geburtstage maximal zeitgleich stattfinden können.
    */
   birthdayMaxCountSimultanous: number;

   /**
    * Gibt eine abweichende Anzahl gleichzeitig stattfindender Geburtstage an.
    */
   birthdayAltMaxCountSimultanous: number | null;

   /**
    * Gibt die Monate an, an denen die abweichende Anzahl gleichzeitig stattfindender Geburtstage gilt.
    */
   birthdayAltMaxCountSimultanousMonths: Month[] | null;

   /**
    * Gibt an wie viele Tage nach Rechnungstellung mindestens gewartet werden soll, bis das Geld abgebucht wird.
    * Normalerweise wird zusätzlich zu den angegebenen Tagen bis zum nächsten Werktag gewartet.
    */
   collectionDateDaysAfterInvoiceDate: number,

   /**
    * Gibt an wie viele Tage vor dem Auslaufen einer Mitgliedschaft diese durch einen Mitgliedsbeitrag verlängert werden soll.
    */
   membershipPaymentDaysAhead: number;

   /**
    * Gibt den Standardpreis für eine jährlichte Mitgliedschaft in Eurocent an.
    */
   membershipDefaultAnnualPrice: number;

   /**
    * Gibt an, wie viele Tage vor einem Geburtstag die Rechnung fällig wird. 0 bedeutet am Geburtstagdatum selber, negative Werte bedeuten nach dem Geburtstag erst.
    */
   birthdayBookingReadyForPaymentDaysAhead: number;

   /**
    * Gibt an, wie viele Tage vor einer Gruppenbuchung die Rechnung fällig wird. 0 bedeutet am Buchungstag selber, negative Werte bedeuten nach dem Buchungstag erst.
    */
   groupBookingReadyForPaymentDaysAhead: number;

   /**
    * Gibt an, wie viele Tage vor einer Ferienbetreuung die Rechnung fällig wird. 0 bedeutet am ersten Ferienbetreuungstag selber, negative Werte bedeuten nach dem ersten Ferienbetreuungstag erst.
    */
   holidayBookingReadyForPaymentDaysAhead: number;

   /**
    * Gibt an, ob bei holidayBookings die Abfrage Alleine kommen/gehen angezeigt werden soll.
    */
   holidayBookingShowAllowToComeGoAlone: boolean;

   /**
    * Gibt an, wie viele Tage vor einem Kurs die Rechnung fällig wird. 0 bedeutet am ersten Kurstag selber, negative Werte bedeuten nach dem ersten Kurstag erst.
    */
   workshopBookingReadyForPaymentDaysAhead: number;

   /**
    * Gibt an, wie viele Tage vor einer Veranstaltung die Rechnung fällig wird. 0 bedeutet am ersten Kurstag selber, negative Werte bedeuten nach dem ersten Kurstag erst.
    */
   eventBookingReadyForPaymentDaysAhead: number;

   /**
    * Gibt an, ob nach dem Anmelden eines neuen Nutzers beim Newsletter eine E-Mail an die Verwaltung geschickt werden soll.
    */
   mailingSendConfirmationMailToManagement: boolean;

   /**
    * Wenn aktiviert, werden für die Verwaltung alle RentalConditions zur Auswahl angeboten, unabhängig von Personen-Beschränkungen.
    */
   rentalShowAllConditionsIfUserPermissionAble: boolean;

   /**
    * Mögliche Kategorien eines Verleihbuchungsnutzers.
    */
   rentalAccountTypes: string[];

   /**
    * Gibt an wie knapp vor Beginn des Verleihzeitraums Anfragen noch gemacht werden können.
    */
   rentalCanRequestUntilDaysAhead: number;

   /**
    * Gibt an wie weit in die Zukunft Verleihgegenstände gebucht werden können.
    */
   rentalBookingDaysIntoFuture: number;

   /**
    * Gibt an bis zu welchem Datum in der Zukunft Verleihgegenstände gebucht werden können. Bei null ist kein Limit gegeben
    */
   rentalBookingUntilDate: string | null;

   /**
    * Wenn aktiviert, werden im öffentlichen Verleih Bereich alle Produkte und Preise geladen ohne Beachtung von restrictToTypes.
    */
   rentalPublicIgnoreRestrictToTypes: boolean;

   /**
    * Wenn aktiviert, wird im öffentlichen Verleih Bereich ein Popup angezeigt.
    */
   rentalPublicShowStartPopup: boolean;

   /**
    * Gibt an ob Abhol-/Rückgabe-Zeit abgefragt werden soll.
    */
   rentalRequestPickupAndReturnTime: boolean;

   /**
    * Gibt die Minuten an, wie lange ein Abhol-/Rückgabe-Zeitslot sein soll.
    */
   rentalTimeSlotsDuration: number;

   /**
    * Gibt an, ob beim Export von Rechnungen, nur Rechnungen mit der gleichen MwSt. Befreiungsbegründung zusammengefasst werden sollen
    */
   paymentExportByRemarksVATExemption: boolean;

   /**
    * Gibt an, ob eine SEPA XML Datei beim Zahlungsexport ausgegeben werden soll.
    */
   paymentExportSepaXmlEnabled: boolean;

   /**
    * Gibt an, ob SAP PSCD Dateien beim Zahlungsexport ausgegeben werden sollen.
    */
   paymentExportSAPPSCDEnabled: boolean;

   /**
    * Gibt an, welche Kategorien an Mehrwertsteuer es gibt.
    * Wenn das Array leer ist, wird keine Mehrwertsteuerauswahl angeboten.
    */
   priceVATCategories: VATCategoryJson[];

   /**
    * Wenn aktiviert, dann werden allgemine CategoryTypes genutzt und nicht aufgeteilt nach CategoryTypeType.
    */
   useGeneralCategoryTypes: boolean;

   /**
    * Wenn aktiviert, werden Kategorien nach ihrer Gruppierung sortiert und aufgeteilt.
    */
   useCategoryTypeGrouping: boolean;

   /**
    * Aktiviert die Möglichkeit Bilder von Ausweisen/Führerscheinen bei Buchung zu verlangen.
    */
   enableIdDocumentUploads: boolean;
}

export type SettingsPublicJson = Pick<SettingsJson, "holidayBookingShowAllowToComeGoAlone" | "rentalPublicShowStartPopup" | "rentalRequestPickupAndReturnTime" | "useGeneralCategoryTypes" | "enableIdDocumentUploads">;

export function validateSettingsJson(item: SettingsJson) {
   return new Validation(item)
      .validateAllUsingFunction("birthdayTimeSlots", validateBirthdayTimeSlotJson)
      .validateAllUsingFunction("businessHours", validateBusinessHoursJson)
      .validateInteger("deleteUserDataAfterDays", 1, 1000)
      .validateInteger("birthdaysCanRequestUntilDaysAhead", 0, 90)
      .validateInteger("birthdaysCanConfirmUntilDaysAhead", 0, 90)
      .validateInteger("groupBookingCanRequestUntilDaysAhead", 0, 90)
      .validateInteger("groupBookingCanConfirmUntilDaysAhead", 0, 90)
      .validateInteger("eventBookingCanRequestUntilDaysAhead", 0, 90)
      .validateInteger("eventBookingCanModifyUntilDaysAhead", 0, 90)
      .validateInteger("collectionDateDaysAfterInvoiceDate", 1, 31)
      .validateInteger("birthdayMaxCountSimultanous", 1, 100)
      .validateInteger("birthdayAltMaxCountSimultanous", 0, 100, true)
      .validateInteger("membershipPaymentDaysAhead", 1, 100)
      .validateInteger("membershipDefaultAnnualPrice", 0)
      .validateInteger("birthdayBookingReadyForPaymentDaysAhead", -100, 100)
      .validateInteger("groupBookingReadyForPaymentDaysAhead", -100, 100)
      .validateInteger("holidayBookingReadyForPaymentDaysAhead", -100, 100)
      .validateInteger("workshopBookingReadyForPaymentDaysAhead", -100, 100)
      .validateInteger("eventBookingReadyForPaymentDaysAhead", -100, 100)
      .validateInteger("rentalCanRequestUntilDaysAhead", 0, 90)
      .validateInteger("rentalBookingDaysIntoFuture", -1, 365 * 5)
      .validateInteger("rentalTimeSlotsDuration", 1, 60 * 24)
      .validateDate("rentalBookingUntilDate", true)
      .finish();
}

export function getRentalTimeSlotsAtWeekDay(weekDay: WeekDay, businessHours: BusinessHoursJson[], timeSlotsDuration: number): RentalTimeSlotJson[] {
   let timeSlots: RentalTimeSlotJson[] = [];

   if(timeSlotsDuration > 0) {
      for(let item of businessHours) {
         if(item.weekDay == weekDay) {
            let beginTime = DateFormatter.parseTime(item.beginTime);
            const endTime = DateFormatter.parseTime(item.endTime);
            
            if(beginTime && endTime) {
               while(beginTime < endTime) {
                  const timeSlotBegin = new Date(beginTime);
                  const timeSlotEnd = new Date(beginTime.getTime() + timeSlotsDuration * 60000);

                  if(timeSlotEnd > endTime) break;

                  timeSlots.push({
                     beginTime: DateFormatter.formatTime(timeSlotBegin),
                     endTime: DateFormatter.formatTime(timeSlotEnd),
                  })

                  beginTime.setTime(timeSlotEnd.getTime());
               }
            }
         }
      }
   }

   return timeSlots;
}